class SleepSystemUpsell extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-upsell-product]').forEach((card) => {
      const button = card.querySelector('[data-add-upsell]');
      const variantId = card.dataset.variantId;
      if (!button || !variantId) return;
      button.addEventListener('click', () => this.addVariant(card, variantId));
    });
  }

  async addVariant(card, variantId) {
    const button = card.querySelector('[data-add-upsell]');
    const spinner = card.querySelector('.loading__spinner');
    const message = card.querySelector('[data-message]');
    if (!button || button.disabled) return;

    button.disabled = true;
    spinner?.classList.remove('hidden');
    if (message) message.textContent = '';

    const sections = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    if (document.getElementById('main-cart-items')) sections.push('main-cart-items', 'main-cart-footer', 'cart-live-region-text');

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to add this item.');

      if (state.sections) await this.renderSections(state.sections);

      // Shopify can return a stale main-cart-footer fragment in the same
      // /cart/add.js response when app blocks or accelerated checkout buttons
      // are present. Fetch the footer once more after the mutation so subtotal
      // and financing values always reflect the newly-added accessory.
      await this.refreshCartFooter();

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (message) message.textContent = 'Added to your cart.';
      button.querySelector('[data-button-text]').textContent = 'Added';
      card.classList.add('is-added');
    } catch (error) {
      if (message) message.textContent = error.message || 'Unable to add this item. Please try again.';
      button.disabled = false;
    } finally {
      spinner?.classList.add('hidden');
    }
  }

  async renderSections(sections) {
    const parser = new DOMParser();

    // Keep the existing checkout and accelerated-payment controls mounted.
    // Shopify initializes those controls on page load; replacing the entire
    // cart footer after an Ajax upsell can leave them blank until a refresh.
    if (sections['main-cart-footer']) {
      const parsedFooter = parser.parseFromString(sections['main-cart-footer'], 'text/html');
      const nextFooter = parsedFooter.querySelector('#main-cart-footer');
      const currentFooter = document.querySelector('#main-cart-footer');

      if (nextFooter && currentFooter) {
        const currentFinancing = currentFooter.querySelector('.cart__financing-column');
        const nextFinancing = nextFooter.querySelector('.cart__financing-column');
        if (currentFinancing && nextFinancing) currentFinancing.replaceWith(nextFinancing);

        const currentSummary = currentFooter.querySelector('.cart__summary-card');
        const nextSummary = nextFooter.querySelector('.cart__summary-card');
        if (currentSummary && nextSummary) currentSummary.replaceWith(nextSummary);

        currentFooter.classList.toggle('is-empty', nextFooter.classList.contains('is-empty'));
      }
    }

    const mappings = [
      ['cart-icon-bubble', '#shopify-section-cart-icon-bubble'],
      ['cart-drawer', '#CartDrawer'],
      ['main-cart-items', '#main-cart-items'],
      ['cart-live-region-text', '#shopify-section-cart-live-region-text'],
    ];

    mappings.forEach(([sectionId, selector]) => {
      if (!sections[sectionId]) return;
      const current = document.querySelector(selector);
      const parsed = parser.parseFromString(sections[sectionId], 'text/html');
      const replacement = parsed.querySelector(selector) || parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
      if (current && replacement) current.replaceWith(replacement);
    });

    // Re-run Shopify's payment-button initializer as a safe fallback for
    // browsers or apps that inject accelerated checkout controls asynchronously.
    if (window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }
  }

  async refreshCartFooter() {
    const currentFooter = document.querySelector('#main-cart-footer');
    if (!currentFooter) return;

    try {
      const sectionId = currentFooter.dataset.id || 'main-cart-footer';
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_cart_refresh', Date.now().toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'text/html' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Unable to refresh cart totals.');

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextFooter = parsed.querySelector('#main-cart-footer');
      if (!nextFooter) throw new Error('Cart footer section was not returned.');

      this.replaceFooterContent(currentFooter, nextFooter);
    } catch (error) {
      console.error('[Novus Sleep] Cart total refresh failed:', error);
      await this.refreshTotalsFromCartJson();
    }
  }

  replaceFooterContent(currentFooter, nextFooter) {
    const currentFinancing = currentFooter.querySelector('.cart__financing-column');
    const nextFinancing = nextFooter.querySelector('.cart__financing-column');
    if (currentFinancing && nextFinancing) currentFinancing.replaceWith(nextFinancing);

    const currentSummary = currentFooter.querySelector('.cart__summary-card');
    const nextSummary = nextFooter.querySelector('.cart__summary-card');
    if (currentSummary && nextSummary) currentSummary.replaceWith(nextSummary);

    currentFooter.classList.toggle('is-empty', nextFooter.classList.contains('is-empty'));

    if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
  }

  async refreshTotalsFromCartJson() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return;

    const cart = await response.json();
    const locale = document.documentElement.lang || 'en-US';
    const currency = cart.currency || window.Shopify?.currency?.active || 'USD';
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format((cart.total_price || 0) / 100);

    document.querySelectorAll('#main-cart-footer .totals__total-value').forEach((element) => {
      element.textContent = `${formatted} ${currency}`;
    });
    document.querySelectorAll('#main-cart-footer .cart-synchrony-financing__subtotal strong').forEach((element) => {
      element.textContent = `${formatted} ${currency}`;
    });
  }
}

if (!customElements.get('sleep-system-upsell')) {
  customElements.define('sleep-system-upsell', SleepSystemUpsell);
}
